<template>
  <div class="cd-root" ref="rootEl">
    <slot
        name="trigger"
        :count="cartCount"
        :open="open"
        :toggle="toggleOpen"
        :refresh="refresh"
    />

    <div v-if="open" class="cd-backdrop" :class="{ drawer: isDrawer, dropdown: isDropdown }" @click="onBackdropClick">
      <aside class="cd-modal" :class="{ drawer: isDrawer, dropdown: isDropdown }" @click.stop>
        <div v-if="deleteBusy" class="cd-delete-overlay" aria-live="polite" aria-busy="true">
          <div class="cd-delete-card">
            <span class="cd-delete-spinner" />
            <strong>Bilder werden gelöscht</strong>
          </div>
        </div>
        <header class="cd-head">
          <div class="cd-title">
            <div class="cd-title-row">
              <strong>{{ title }}</strong>
              <span
                  v-if="cartCount > 0"
                  class="cd-badge"
                  :title="`${cartCount} Artikel`"
                  aria-label="Artikel im Warenkorb"
              >
                {{ cartCount }}
              </span>
            </div>

            <span class="cd-sub">
              <span v-if="localMode !== 'all'" class="cd-sub-suffix">{{ modeLabel }}</span>
            </span>
          </div>

          <button class="cd-x" type="button" @click="emitClose" aria-label="Schließen" title="Schließen">
            <X :size="18" />
          </button>
        </header>

        <div v-if="showTabs" class="cd-tabs" role="tablist" aria-label="Ansicht">
          <button
              class="tab"
              type="button"
              :class="{ on: localMode === 'all' }"
              @click="localMode = 'all'"
              role="tab"
              :aria-selected="String(localMode==='all')"
          >
            Alle
          </button>
          <button
              class="tab"
              type="button"
              :class="{ on: localMode === 'images' }"
              @click="localMode = 'images'"
              role="tab"
              :aria-selected="String(localMode==='images')"
          >
            Fotos
          </button>
          <button
              class="tab"
              type="button"
              :class="{ on: localMode === 'products' }"
              @click="localMode = 'products'"
              role="tab"
              :aria-selected="String(localMode==='products')"
          >
            Produkte
          </button>
        </div>

        <section class="cd-body">
          <div v-if="toast.toasts.value.length" class="toast-wrap" aria-live="polite">
            <div v-for="t in toast.toasts.value" :key="t.id" class="toast" :class="t.type">
              {{ t.text }}
            </div>
          </div>
          <div v-if="cart.loading || isBootLoading" class="cd-loading">Lade …</div>
          <div v-else-if="!filteredGroups.length" class="cd-empty">
            Noch keine Artikel{{ localMode === 'all' ? '' : ` (${modeLabel})` }} im Warenkorb.
          </div>

          <div v-else class="cd-list">
            <article v-for="group in filteredGroups" :key="group.groupKey" class="cd-group">
              <button class="cd-group-head" type="button" @click="toggleGroup(group.groupKey)">


                <div class="cd-main">
                  <div class="cd-row1">
                    <div class="cd-name" :title="group.title">
                      {{ group.title }}
                    </div>
                    <div class="cd-price">
                      {{ formatCents(group.lineTotalCents) }}
                    </div>
                  </div>
                  <div class="cd-subline">
                    <span class="cd-type">{{ group.subtitle || group.typeLabel || 'Artikel' }}</span>
                    <span class="cd-qty-total">{{ toInt(group.totalQuantity || 0) }} Stück</span>
                  </div>
                  <div v-if="group.meta?.length" class="cd-meta">
                    <span v-for="m in group.meta" :key="m" class="cd-meta-item">{{ m }}</span>
                  </div>
                </div>
                <span class="cd-group-chevron" :class="{ open: isGroupOpen(group.groupKey) }">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </span>
              </button>

                <div v-if="isGroupOpen(group.groupKey)" class="cd-group-items">
                <div v-for="it in group.items || []" :key="it.id ?? `${group.groupKey}-${it.title}`" class="cd-item">
                  <div class="cd-item-thumb" :style="itemThumbStyle(it)" :class="{ 'is-grayscale': itemIsGrayscale(it), 'is-loading': isThumbPending(it) }">
                    <img
                      v-if="thumbSrc(it)"
                      :key="thumbSrc(it)"
                      :src="thumbSrc(it)!"
                      alt=""
                      loading="lazy"
                      decoding="async"
                      @load="markThumbLoaded(it)"
                      @error="markThumbLoaded(it)"
                    />
                    <div v-if="isThumbPending(it)" class="cd-thumb-spinner" aria-hidden="true"></div>
                  </div>

                  <div class="cd-main">
                    <div class="cd-row1">
                      <div class="cd-title-block">
                        <div class="cd-name" :title="it.title">{{ it.title }}</div>
                        <div v-if="it.meta?.length" class="cd-meta">
                          <span v-for="m in it.meta" :key="m" class="cd-meta-item">{{ m }}</span>
                        </div>
                      </div>

                      <div class="cd-item-actions">
                        <button
                            v-if="canEditInEditor(it)"
                            class="cd-icon"
                            type="button"
                            @click="openInEditor(it)"
                            :disabled="cart.loading"
                            title="Im Editor bearbeiten"
                            aria-label="Im Editor bearbeiten"
                        >
                          <Pencil :size="16" />
                        </button>

                        <button
                            class="cd-icon danger"
                            type="button"
                            @click="onRemove(it)"
                            :disabled="cart.loading || !it.id"
                            title="Entfernen"
                            aria-label="Entfernen"
                        >
                          <Trash2 :size="16" />
                        </button>
                      </div>
                    </div>

                    <div
                      v-for="sub in itemSubItems(it)"
                      :key="`${it.id ?? it.imageId ?? it.title}-${sub.type}-${sub.label}`"
                      class="cd-license-row"
                      :class="{ 'cd-license-row--fee': sub.type === 'processing_fee' }"
                    >
                      <span class="cd-license-label">
                        {{ sub.label }}
                        <small v-if="sub.meta" class="cd-license-meta">{{ sub.meta }}</small>
                      </span>
                      <strong class="cd-license-price">{{ formatCents(sub.lineTotalCents) }}</strong>
                    </div>

                    <div class="cd-row2">
                      <div class="cd-qty">
                        <button
                            class="cd-icon"
                            type="button"
                            @click="stepQty(it, -1)"
                            :disabled="cart.loading || !it.id || toInt(it.quantity) <= 1"
                            aria-label="Minus"
                            title="Minus"
                        >
                          <Minus :size="16" />
                        </button>

                        <input
                            class="cd-qty-input"
                            type="number"
                            min="1"
                            :value="toInt(it.quantity)"
                            @change="onQtyInput(it, $event)"
                            :disabled="cart.loading || !it.id"
                        />

                        <button
                            class="cd-icon"
                            type="button"
                            @click="stepQty(it, +1)"
                            :disabled="cart.loading || !it.id"
                            aria-label="Plus"
                            title="Plus"
                        >
                          <Plus :size="16" />
                        </button>
                      </div>

                      <div class="cd-price">
                        {{ formatCents(it.lineTotalCents) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        <footer class="cd-foot">
          <div class="cd-totals">
            <div class="cd-total-row">
              <span class="muted">Netto</span>
              <strong>{{ formatCents(netTotalCentsSafe) }}</strong>
            </div>
            <div class="cd-total-row">
              <span class="muted">MwSt. ({{ taxRateLabel }})</span>
              <strong>{{ formatCents(taxTotalCentsSafe) }}</strong>
            </div>
            <div class="cd-total-row cd-total-row--grand">
              <span class="muted">Brutto</span>
              <strong>{{ formatCents(grossTotalCentsSafe) }}</strong>
            </div>
          </div>

          <div class="cd-actions">
            <button class="btn primary" type="button" @click="handleCheckout" :disabled="!cart.items.length">
              <CreditCard :size="16" /> Zum Warenkorb
            </button>
            <a class="btn cd-accessories-link" href="/shop/kategorien">
              Fotozubehör
            </a>
          </div>
        </footer>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref, onBeforeUnmount, nextTick } from 'vue'
import { useCartStore, type CartItem } from '../store/cart'
import { useToast } from '../composables/useToast'
import { X, Trash2, Minus, Plus, CreditCard, Pencil } from 'lucide-vue-next'
import { getWidgetConfig } from '../runtime/widgetConfig'
import { useImageDB } from '../utils/imageDb'
import { useEditorNav } from '../composables/useEditorNav'
import { deleteImageRecordsRemoteBulk } from '../services/uploadService'

const props = defineProps<{
  open: boolean
  thumbMap?: Record<string, string>
  autoLoad?: boolean
  mode?: 'all' | 'images' | 'products'
  showTabs?: boolean
  title?: string
  checkoutHref?: string
  variant?: 'popup' | 'drawer' | 'dropdown'
  groupsOpenByDefault?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', v: boolean): void
  (e: 'checkout'): void
  (e: 'update:mode', v: 'all' | 'images' | 'products'): void
}>()

const cart = useCartStore()
const toast = useToast()
const db = useImageDB('imageDB')
const { goToEditor } = useEditorNav()
const EDITOR_IMAGES_CHANGED_EVENT = 'pfe:editor-images-changed'

const title = computed(() => props.title || 'Warenkorb')
const effectiveVariant = computed<'popup' | 'drawer' | 'dropdown'>(() => props.variant || 'dropdown')
const isDrawer = computed(() => effectiveVariant.value === 'drawer')
const isDropdown = computed(() => effectiveVariant.value === 'dropdown')
const rootEl = ref<HTMLElement | null>(null)
const openGroups = ref<Record<string, boolean>>({})
const deleteBusy = ref(false)
const handleCartResume = () => {
  void cart.refreshCartIfSessionExists().catch(() => null)
}

const isBootLoading = ref(false)
const didBootLoad = ref(false)
const thumbLoadState = ref<Record<string, boolean>>({})
let thumbRefreshTimer: number | null = null
let thumbRefreshAttempts = 0
const THUMB_REFRESH_MAX_ATTEMPTS = 5
const isUnmounted = ref(false)

function emitClose() {
  emit('update:open', false)
}
function toggleOpen() {
  emit('update:open', !props.open)
}
function onBackdropClick() {
  if (isDropdown.value) return
  emitClose()
}
function onDocPointerDown(ev: MouseEvent) {
  if (!props.open || !isDropdown.value) return
  const host = rootEl.value
  if (!host) return
  const target = ev.target as Node | null
  if (!target) return
  if (host.contains(target)) return
  emitClose()
}

function navigateWithCartSession(href: string) {
  let navigated = false
  const commit = () => {
    if (navigated) return
    navigated = true
    window.location.href = href
  }
  window.setTimeout(commit, 180)
  void cart.primeCheckoutSession().catch(() => {}).finally(commit)
}

function handleCheckout() {
  const href = String(props.checkoutHref || '').trim()
  if (href) {
    navigateWithCartSession(href)
    return
  }
  emit('checkout')
}

const localMode = ref<'all' | 'images' | 'products'>(props.mode || 'all')
const showTabs = computed(() => props.showTabs !== false)

watch(
    () => props.mode,
    (m) => {
      if (!m) return
      localMode.value = m
    },
    { immediate: true },
)

watch(
    () => localMode.value,
    (m) => emit('update:mode', m),
)

onBeforeUnmount(() => {
  isUnmounted.value = true
  document.removeEventListener('mousedown', onDocPointerDown, true)
  window.removeEventListener('pageshow', handleCartResume)
  window.removeEventListener('focus', handleCartResume)
  if (thumbRefreshTimer != null) {
    window.clearTimeout(thumbRefreshTimer)
    thumbRefreshTimer = null
  }
})

const modeLabel = computed(() => {
  if (localMode.value === 'images') return 'Fotos'
  if (localMode.value === 'products') return 'Produkte'
  return 'Alle'
})

const toCents = (v: any) => {
  const n = Number(v)
  return Number.isFinite(n) ? Math.round(n) : 0
}
const toInt = (v: any) => {
  const n = Number(v)
  return Number.isFinite(n) ? Math.max(1, Math.floor(n)) : 1
}

function formatCents(cents: any) {
  const v = toCents(cents) / 100
  try {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
  } catch {
    return `${v.toFixed(2).replace('.', ',')} €`
  }
}

function resolvedThumb(src?: string | null) {
  const value = String(src || '').trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) return value
  const cfg = getWidgetConfig() as any
  const isEditorUpload = value.startsWith('/editor/upload/')
  const isMediaUpload = value.startsWith('/uploads/')
  const isPublicMedia = value.startsWith('/media/') || value.startsWith('/media/cache/')
  const rawBase = String(isEditorUpload ? (cfg.editorBaseUrl || '') : (cfg.cartBackendBaseUrl || cfg.editorBaseUrl || '')).replace(/\/$/, '')
  const base = isEditorUpload
    ? rawBase.replace(/\/editor$/i, '')
    : (isMediaUpload || isPublicMedia)
      ? rawBase.replace(/\/v1$/i, '').replace(/\/editor$/i, '')
      : rawBase
  return base ? `${base}${value.startsWith('/') ? value : `/${value}`}` : value
}

function thumbStateKey(it: any) {
  const itemKey = String(it?.id ?? it?.imageId ?? it?.title ?? '').trim()
  return `${itemKey}::${thumbSrc(it)}`
}

function thumbSrc(it: any): string {
  return resolvedThumb(itemThumb(it))
}

function isThumbPending(it: any): boolean {
  const src = thumbSrc(it)
  if (!src) return true
  return thumbLoadState.value[thumbStateKey(it)] !== true
}

function markThumbLoaded(it: any) {
  const key = thumbStateKey(it)
  if (thumbLoadState.value[key]) return
  thumbLoadState.value = {
    ...thumbLoadState.value,
    [key]: true,
  }
}

function scheduleThumbRefreshIfNeeded() {
  if (thumbRefreshTimer != null) {
    window.clearTimeout(thumbRefreshTimer)
    thumbRefreshTimer = null
  }
  if (!props.open) return
  const hasMissingThumb = filteredGroups.value.some((group: any) =>
    Array.isArray(group?.items) && group.items.some((it: any) => !thumbSrc(it)),
  )
  if (!hasMissingThumb) {
    // All thumbs present: reset so a future missing set can poll again.
    thumbRefreshAttempts = 0
    return
  }
  // Cap retries so missing thumbs can't trigger endless server polling.
  if (thumbRefreshAttempts >= THUMB_REFRESH_MAX_ATTEMPTS) return
  thumbRefreshAttempts += 1
  thumbRefreshTimer = window.setTimeout(() => {
    thumbRefreshTimer = null
    void cart.refreshCartIfSessionExists().catch(() => null)
  }, 1200)
}

function itemThumbStyle(it: any) {
  return {
    aspectRatio: '1 / 1',
  }
}

function itemIsGrayscale(it: any) {
  const candidates = [
    it?.grayscale,
    it?.preset?.grayscale,
    it?.payload?.preset?.grayscale,
    it?.payload?.metadata?.edits?.preset?.grayscale,
    it?.payload?.metadata?.edits?.grayscale,
    it?.payload?.edits?.preset?.grayscale,
    it?.payload?.edits?.grayscale,
    it?.payload?.grayscale,
  ]
  return candidates.some((value) => value === true || value === 1 || String(value || '').trim() === '1' || String(value || '').trim().toLowerCase() === 'true')
}

function itemThumb(it: any): string {
  const directCartPreview = [
    it?.cartPreviewUrl,
    it?.payload?.cartPreviewUrl,
    it?.preset?.cartPreviewUrl,
  ].map((v) => String(v || '').trim()).find(Boolean)
  if (directCartPreview) return directCartPreview

  const imageId = String(it?.imageId ?? it?.payload?.imageId ?? it?.preset?.imageId ?? '').trim()

  const fullItem = (cart.cart?.items || []).find((candidate: any) => {
    if (it?.id != null && candidate?.id != null && String(candidate.id) === String(it.id)) return true
    const candidateImageId = String(candidate?.imageId ?? candidate?.payload?.imageId ?? candidate?.preset?.imageId ?? '').trim()
    return !!imageId && candidateImageId === imageId
  }) as any

  const fullCartPreview = [
    fullItem?.cartPreviewUrl,
    fullItem?.payload?.cartPreviewUrl,
    fullItem?.preset?.cartPreviewUrl,
  ].map((v) => String(v || '').trim()).find(Boolean) || ''
  if (fullCartPreview) return fullCartPreview

  const directFallback = [
    it?.thumbUrl,
    it?.previewUrl,
    it?.payload?.thumbUrl,
    it?.payload?.previewUrl,
    it?.preset?.thumbUrl,
  ].map((v) => String(v || '').trim()).find(Boolean)
  if (directFallback) return directFallback

  if (imageId && props.thumbMap?.[imageId]) return String(props.thumbMap[imageId] || '').trim()

  return [
    fullItem?.thumbUrl,
    fullItem?.previewUrl,
    fullItem?.payload?.thumbUrl,
    fullItem?.payload?.previewUrl,
    fullItem?.preset?.thumbUrl,
  ].map((v) => String(v || '').trim()).find(Boolean) || ''
}

const subtotalCentsSafe = computed(() => {
  return toCents((cart as any).subtotalCents ?? (cart as any).cart?.subtotalCents)
})
const grossTotalCentsSafe = computed(() => toCents((cart.cart as any)?.totals?.grossTotalCents ?? (cart.cart as any)?.totals?.totalCents ?? subtotalCentsSafe.value))
const taxRateValue = computed(() => {
  const raw = Number((cart.cart as any)?.totals?.taxRate ?? 19)
  return Number.isFinite(raw) && raw >= 0 ? raw : 19
})
const derivedNetTotalCents = computed(() => {
  const gross = grossTotalCentsSafe.value
  const rate = taxRateValue.value
  if (gross <= 0) return 0
  return Math.round(gross / (1 + rate / 100))
})
const netTotalCentsSafe = computed(() => {
  const explicit = (cart.cart as any)?.totals?.netTotalCents
  const value = toCents(explicit)
  return value > 0 ? value : derivedNetTotalCents.value
})
const taxTotalCentsSafe = computed(() => {
  const explicit = (cart.cart as any)?.totals?.taxTotalCents
  const value = toCents(explicit)
  if (value > 0) return value
  return Math.max(0, grossTotalCentsSafe.value - netTotalCentsSafe.value)
})
const taxRateLabel = computed(() => `${taxRateValue.value.toFixed(0)}%`)

const rawCartItemsById = computed(() => {
  const map = new Map<string, any>()
  for (const item of (cart.cart?.items || [])) {
    const key = String((item as any)?.id ?? '').trim()
    if (!key) continue
    map.set(key, item)
  }
  return map
})

const displayGroups = computed<any[]>(() => {
  const groups = Array.isArray((cart.cart as any)?.displayGroups) ? (cart.cart as any).displayGroups : []
  return groups.map((group: any) => ({
    ...group,
    items: Array.isArray(group?.items)
      ? group.items.map((it: any) => {
          const raw = rawCartItemsById.value.get(String(it?.id ?? '').trim())
          return raw ? { ...raw, ...it } : it
        })
      : [],
  }))
})

const filteredGroups = computed(() => {
  const list = displayGroups.value || []
  if (localMode.value === 'images') return list.filter((g: any) => String(g?.type || '') === 'image')
  if (localMode.value === 'products') return list.filter((g: any) => String(g?.type || '') === 'product')
  return list
})

watch(filteredGroups, (groups) => {
  const next: Record<string, boolean> = { ...openGroups.value }
  for (const group of groups || []) {
    if (next[group.groupKey] == null) next[group.groupKey] = props.groupsOpenByDefault !== false
  }
  openGroups.value = next
  scheduleThumbRefreshIfNeeded()
}, { immediate: true })

function isGroupOpen(key: string) {
  return openGroups.value[key] !== false
}

function toggleGroup(key: string) {
  openGroups.value = { ...openGroups.value, [key]: !isGroupOpen(key) }
}

function notifyEditorImagesChanged(ids: string[], action: 'updated' | 'removed', patch?: Record<string, any>) {
  const clean = (ids || []).filter(Boolean)
  if (!clean.length) return
  try {
    window.dispatchEvent(new CustomEvent(EDITOR_IMAGES_CHANGED_EVENT, { detail: { ids: clean, action, patch } }))
  } catch {}
}

async function syncImageQuantityToDb(it: any, quantity: number) {
  const imageId = String(it?.imageId ?? it?.payload?.imageId ?? '').trim()
  if (!imageId) return
  const rec: any = await db.get(imageId)
  if (!rec) return
  const nextPreset = {
    ...((rec?.edits?.preset || {}) as any),
    quantity: Math.max(1, Number(quantity || 1)),
  }
  await db.update(imageId, {
    edits: {
      ...(rec?.edits || {}),
      preset: nextPreset,
    } as any,
  } as any)
  notifyEditorImagesChanged([imageId], 'updated', { quantity: Math.max(1, Number(quantity || 1)) })
}

async function removeImageFromDb(it: any) {
  const imageId = String(it?.imageId ?? it?.payload?.imageId ?? '').trim()
  if (!imageId) return
  await db.delete(imageId)
  await deleteImageRecordsRemoteBulk([imageId]).catch(() => {})
  notifyEditorImagesChanged([imageId], 'removed')
}

function canEditInEditor(it: any) {
  return String(it?.imageId ?? it?.payload?.imageId ?? '').trim() !== ''
}

function cartCropToEditorCrop(raw: any) {
  if (!raw || typeof raw !== 'object') return null
  const x = Math.max(0, Math.round(Number(raw.x) || 0))
  const y = Math.max(0, Math.round(Number(raw.y) || 0))
  const w = Math.max(1, Math.round(Number(raw.width ?? raw.w) || 0))
  const h = Math.max(1, Math.round(Number(raw.height ?? raw.h) || 0))
  if (!(w > 0 && h > 0)) return null
  const sourceWidth = Math.max(0, Math.round(Number(raw.sourceWidth) || 0))
  const sourceHeight = Math.max(0, Math.round(Number(raw.sourceHeight) || 0))
  return {
    x,
    y,
    w,
    h,
    ...(sourceWidth > 0 ? { sourceWidth } : {}),
    ...(sourceHeight > 0 ? { sourceHeight } : {}),
  }
}

async function syncCartItemToLocalImageRecord(imageId: string, it: any) {
  const rec: any = await db.get(imageId)
  if (!rec) return

  const itemPreset = ((it?.preset && typeof it.preset === 'object') ? it.preset : null)
    || ((it?.payload?.preset && typeof it.payload.preset === 'object') ? it.payload.preset : null)
    || null
  const itemCrop = cartCropToEditorCrop(itemPreset?.crop)
  const rotate = Number(itemPreset?.rotate ?? rec?.edits?.rotate ?? 0) || 0

  const mergedPreset = {
    ...((rec?.edits?.preset || {}) as any),
    ...(itemPreset || {}),
    ...(Number(it?.productId ?? it?.payload?.productId ?? 0) > 0 ? { productId: Number(it?.productId ?? it?.payload?.productId ?? 0) } : {}),
    ...(Number(it?.payload?.categoryId ?? 0) > 0 ? { categoryId: Number(it?.payload?.categoryId ?? 0) } : {}),
    ...(Number(it?.payload?.rootCategoryId ?? 0) > 0 ? { parentCategoryId: Number(it?.payload?.rootCategoryId ?? 0) } : {}),
  }

  const patch: Record<string, any> = {
    productId: Number(it?.productId ?? it?.payload?.productId ?? rec?.productId ?? 0) || rec?.productId,
    categoryId: Number(it?.payload?.categoryId ?? rec?.categoryId ?? 0) || rec?.categoryId,
    parentCategoryId: Number(it?.payload?.rootCategoryId ?? rec?.parentCategoryId ?? 0) || rec?.parentCategoryId,
    productTitle: String(it?.payload?.productTitle ?? rec?.productTitle ?? '').trim() || rec?.productTitle,
    categoryTitle: String(it?.payload?.categoryTitle ?? rec?.categoryTitle ?? '').trim() || rec?.categoryTitle,
    parentCategoryTitle: String(it?.payload?.rootCategoryTitle ?? rec?.parentCategoryTitle ?? '').trim() || rec?.parentCategoryTitle,
    edits: {
      ...((rec?.edits || {}) as any),
      preset: mergedPreset,
      rotate,
      ...(itemCrop ? { crop: itemCrop } : {}),
    },
  }

  await db.update(imageId, patch as any)
}

function itemSubItems(it: any) {
  const raw = Array.isArray(it?.subItems) ? it.subItems : []
  return raw
    .map((sub: any) => ({
      type: String(sub?.type || '').trim(),
      label: String(sub?.label || '').trim(),
      meta: String(sub?.meta || '').trim(),
      lineTotalCents: Math.max(0, toCents(sub?.lineTotalCents ?? sub?.amountCents ?? 0)),
    }))
    .filter((sub: any) => sub.label && sub.lineTotalCents > 0)
}

async function openInEditor(it: any) {
  const imageId = String(it?.imageId ?? it?.payload?.imageId ?? '').trim()
  if (!imageId) return

  const projectCode = String(it?.payload?.editorProjectId ?? '').trim() || undefined

  try {
    await goToEditor({
      type: 'multi',
      projectCode,
      imageId,
    })
    emitClose()
  } catch (e: any) {
    toast.error(e?.message ?? 'Bild konnte nicht im Editor geöffnet werden')
  }
}

const cartCount = computed(() => {
  return filteredGroups.value.reduce((sum: number, g: any) => sum + toInt((g as any).totalQuantity ?? 0), 0)
})

async function refresh() {
  try {
    await cart.fetchCart()
    didBootLoad.value = true
  } catch (e: any) {
    toast.error(e?.data?.message ?? e?.message ?? 'Cart konnte nicht geladen werden')
  }
}

async function bootLoadIfNeeded() {
  if (didBootLoad.value) return
  isBootLoading.value = true
  try {
    await refresh()
  } finally {
    isBootLoading.value = false
  }
}

async function onRemove(it: CartItem) {
  const isImage = String((it as any)?.type || (it as any)?.kind || '') === 'image' || String((it as any)?.imageId || '') !== ''
  if (isImage) deleteBusy.value = true
  try {
    await cart.removeItem((it as any).id)
    if (isImage) {
      await removeImageFromDb(it as any)
    }
    toast.success('Entfernt')
  } catch (e: any) {
    toast.error(e?.data?.message ?? e?.message ?? 'Konnte nicht entfernen')
  } finally {
    if (isImage) deleteBusy.value = false
  }
}

async function stepQty(it: CartItem, delta: number) {
  const next = Math.max(1, toInt((it as any).quantity || 1) + delta)
  try {
    await cart.setQuantity((it as any).id, next)
    if (String((it as any)?.type || (it as any)?.kind || '') === 'image' || String((it as any)?.imageId || '') !== '') {
      await syncImageQuantityToDb(it as any, next)
    }
  } catch (e: any) {
    toast.error(e?.data?.message ?? e?.message ?? 'Menge konnte nicht geändert werden')
  }
}

async function onQtyInput(it: CartItem, ev: Event) {
  const raw = (ev.target as HTMLInputElement).value
  const next = Math.max(1, toInt(raw || 1))
  try {
    await cart.setQuantity((it as any).id, next)
    if (String((it as any)?.type || (it as any)?.kind || '') === 'image' || String((it as any)?.imageId || '') !== '') {
      await syncImageQuantityToDb(it as any, next)
    }
  } catch (e: any) {
    toast.error(e?.data?.message ?? e?.message ?? 'Menge konnte nicht geändert werden')
  }
}

onMounted(async () => {
  if (props.autoLoad !== false) await bootLoadIfNeeded()
  window.addEventListener('pageshow', handleCartResume)
  window.addEventListener('focus', handleCartResume)
})

watch(
    () => props.open,
    async (v) => {
      if (isDropdown.value) {
        document.removeEventListener('mousedown', onDocPointerDown, true)
        if (v) {
          await nextTick()
          if (isUnmounted.value) return
          document.addEventListener('mousedown', onDocPointerDown, true)
        }
      }
      if (!v) return
      if (props.autoLoad === false) return
      if (!cart.hasStoredSession()) return
      await refresh()
      scheduleThumbRefreshIfNeeded()
    },
    { immediate: true },
)
</script>

<style scoped lang="scss">
.cd-root{
  position:relative;
}
.cd-backdrop{
  position:fixed;
  inset:0;
  background:rgba(15,23,42,.46);
  z-index:2400;
  display:grid;
  place-items:center;
  padding:16px;
}
.cd-backdrop.drawer{
  display:flex;
  align-items:stretch;
  justify-content:flex-end;
  padding:0;
}
.cd-backdrop.dropdown{
  position:absolute;
  inset:auto 0 auto auto;
  top: calc(100% + 10px);
  right: 0;
  background: transparent;
  display:block;
  padding:0;
}
.cd-modal{
  width:min(920px, 96vw);
  height:min(84vh, 760px);
  max-height:100dvh;
  background:#fff;
  border:1px solid rgba(15,23,42,.10);
  box-shadow: 0 22px 70px rgba(2,6,23,.25);
  display:flex;
  flex-direction:column;
  overflow:hidden;
  position:relative;
}
.cd-modal.drawer{
  width:min(460px, 100vw);
  height:100dvh;
  border-top:none;
  border-right:none;
  border-bottom:none;
  box-shadow: -14px 0 40px rgba(2,6,23,.25);
}
.cd-modal.dropdown{
  width:min(700px, 90vw);
  height:min(68vh, 620px);
  border-radius:12px;
}
.cd-modal.dropdown .cd-head{
  padding:10px 12px 8px;
}
.cd-modal.dropdown .cd-tabs{
  padding: 8px 12px;
}
.cd-modal.dropdown .tab{
  height: 30px;
  padding: 0 12px;
  font-size: 11px;
}
.cd-modal.dropdown .cd-body{
  padding:10px 12px;
}
.cd-modal.dropdown .cd-list{
  gap:10px;
}
.cd-modal.dropdown .cd-item{
  padding:8px;
  gap:8px;
}
.cd-modal.dropdown .cd-thumb{
  width:56px;
  height:56px;
}
.cd-modal.dropdown .cd-main{
  gap:6px;
}
.cd-modal.dropdown .cd-name{
  font-size:12px;
}
.cd-modal.dropdown .cd-icon{
  width:28px;
  height:28px;
}
.cd-modal.dropdown .cd-meta-item{
  font-size:11px;
}
.cd-modal.dropdown .cd-qty{
  gap:6px;
}
.cd-modal.dropdown .cd-qty-input{
  width:56px;
  height:28px;
  padding:0 8px;
  font-size:12px;
}
.cd-modal.dropdown .cd-price{
  font-size:12px;
}
.cd-modal.dropdown .cd-foot{
  padding:10px 12px 12px;
  gap:10px;
}
.cd-modal.dropdown .btn{
  height:32px;
  padding:0 10px;
  font-size:12px;
}
.cd-head{ padding:14px 14px 10px; border-bottom:1px solid rgba(15,23,42,.10); display:flex; align-items:center; justify-content:space-between; gap:10px; }
.cd-title{ display:flex; flex-direction:column; gap:2px; min-width:0; }
.cd-title strong{ font-size:14px; }
.cd-sub{ font-size:12px; color:rgba(15,23,42,.55); display:flex; gap:6px; flex-wrap:wrap; }
.cd-sub-suffix{ color: rgba(15,23,42,.45); }

.cd-title-row{ display:flex; align-items:center; gap:10px; }
.cd-badge{
  min-width:22px;
  height:22px;
  padding:0 8px;
  border-radius:999px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size:12px;
  font-weight: 700;
  color:#fff;
  background: rgba(239,68,68,1);
  box-shadow:0 8px 18px rgba(239,68,68,.25);
}

.cd-x{ height:36px; width:36px; border:1px solid rgba(15,23,42,.12); background:rgba(15,23,42,.03); display:grid; place-items:center; cursor:pointer; }

.cd-tabs{
  display:flex;
  gap:8px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(15,23,42,.10);
  flex-wrap: wrap;
}
.tab{
  height: 34px;
  padding: 0 14px;
  border: 1px solid rgba(15,23,42,.12);
  background: #fff;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
  border-radius: 999px;
}
.tab.on{
  border-color: rgba(239,68,68,.28);
  background: rgba(239,68,68,.12);
  color: rgba(127, 29, 29, 1);
}

.cd-body{
  padding:12px 14px;
  overflow-y:auto;
  overflow-x:hidden;
  flex:1 1 auto;
  min-height:0;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(15,23,42,.18) transparent;
}
.cd-body::-webkit-scrollbar{
  width:10px;
}
.cd-body::-webkit-scrollbar-track{
  background:transparent;
}
.cd-body::-webkit-scrollbar-thumb{
  background:rgba(15,23,42,.18);
  border-radius:999px;
  border:2px solid transparent;
  background-clip:padding-box;
}
.cd-loading, .cd-empty{ color:rgba(15,23,42,.55); font-size:13px; padding:10px 2px; }
.cd-delete-overlay{
  position:absolute;
  inset:0;
  z-index:5;
  background: rgba(255,255,255,.68);
  backdrop-filter: blur(2px);
  display:grid;
  place-items:center;
}
.cd-delete-card{
  display:flex;
  align-items:center;
  gap:12px;
  padding:16px 20px;
  border-radius:4px;
  background:#fff;
  border:1px solid rgba(15,23,42,.08);
  box-shadow: 0 18px 40px rgba(15,23,42,.12);
}
.cd-delete-spinner{
  width:18px;
  height:18px;
  border-radius:999px;
  border:2px solid rgba(15,23,42,.16);
  border-top-color:#e30b17;
  animation: cd-delete-spin .8s linear infinite;
}
@keyframes cd-delete-spin{
  to { transform: rotate(360deg); }
}

.cd-list{ display:flex; flex-direction:column; gap:12px; }

.cd-group{
  display:flex;
  flex-direction:column;
  gap:10px;
  border:1px solid rgba(15,23,42,.10);
  padding:10px;
  border-radius: 3px;
  background:#fff;
}
.cd-group-head{
  display:flex;
  gap:10px;
  width:100%;
  border:0;
  background:transparent;
  text-align:left;
  padding:0;
  cursor:pointer;
  align-items:center;
}
.cd-group-items{
  display:flex;
  flex-direction:column;
  gap:8px;
}
.cd-item{
  display:flex;
  gap:10px;
  padding:8px;
  background:transparent;
}
.cd-thumb{ width:64px; height:64px; overflow:hidden; background:rgba(15,23,42,.04); display:grid; place-items:center; flex:0 0 auto; }
.cd-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
.cd-item-thumb{ width:50px; height:auto; min-width:50px; max-width:50px; overflow:visible; background:transparent; display:grid; place-items:center; align-self:center; flex:0 0 50px; border-radius:0; position:relative; box-sizing:border-box; }
.cd-item-thumb img{ width:100%; height:100%; object-fit:contain; object-position:center; display:block; background:transparent; }
.cd-item-thumb.is-grayscale img{ filter: grayscale(1); }
.cd-thumb-spinner{
  width:18px;
  height:18px;
  border-radius:999px;
  border:2px solid rgba(15,23,42,.12);
  border-top-color: var(--pfe-color-accent, #e30b17);
  animation: cd-thumb-spin .9s linear infinite;
}
.cd-thumb,
.cd-item-thumb{
  border:0;
  border-radius:0;
}
.cd-item-thumb.is-loading img{
  opacity:0;
}
@keyframes cd-thumb-spin {
  to { transform: rotate(360deg); }
}

.cd-main{ flex:1 1 auto; min-width:0; display:flex; flex-direction:column; gap:8px; }
.cd-row1{ display:flex; align-items:flex-start; gap:10px; justify-content:space-between; }
.cd-title-block{ display:flex; flex-direction:column; gap:4px; min-width:0; flex:1 1 auto; }
.cd-item-actions{ display:flex; align-items:center; gap:6px; flex:0 0 auto; }
.cd-name{ font-size:13px; font-weight: 700; color:rgba(15,23,42,.86); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cd-subline{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.cd-type{ font-size:12px; font-weight:700; color:rgba(15,23,42,.55); }
.cd-qty-total{ font-size:12px; color:rgba(15,23,42,.48); }
.cd-group-chevron{
  flex:0 0 auto;
  color: rgba(15,23,42,.42);
  transition: transform .16s ease;
}
.cd-group-chevron.open{
  transform: rotate(180deg);
}
.cd-icon{ width:32px; height:32px; border:1px solid rgba(15,23,42,.12); background:#fff; display:grid; place-items:center; cursor:pointer; }
.cd-icon.danger{ border-color: rgba(239,68,68,.28); color:#b91c1c; background: rgba(239,68,68,.06); }
.cd-icon:disabled{ opacity:.5; cursor:not-allowed; }

.cd-meta{ display:flex; flex-wrap:wrap; gap:0; row-gap:1px; }
.cd-meta-item{ font-size:11px; font-weight:400; color: rgba(15,23,42,.56); }
.cd-meta-item + .cd-meta-item::before{ content:"•"; margin:0 3px; color: rgba(15,23,42,.28); }
.cd-license-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  margin-top:6px;
  padding-top:7px;
  border-top:1px solid rgba(15,23,42,.08);
}
.cd-license-label{
  font-size:12px;
  font-weight:700;
  color: rgba(15,23,42,.66);
}
.cd-license-price{
  font-size:12px;
  font-weight:700;
  color:#111827;
  white-space:nowrap;
}

.cd-row2{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
.cd-qty{ display:flex; align-items:center; gap:8px; }
.cd-qty-input{
  width:64px; height:32px;
  border:1px solid rgba(15,23,42,.12);
  padding:0 10px;
  font-weight: 700; color: rgba(15,23,42,.85);
  outline:none;
  appearance: textfield;
  -moz-appearance: textfield;
}
.cd-qty-input::-webkit-outer-spin-button,
.cd-qty-input::-webkit-inner-spin-button{
  -webkit-appearance: none;
  margin: 0;
}

.cd-price{ font-weight: 700; color: rgba(15,23,42,.88); white-space:nowrap; font-size:13px; }

.cd-foot{
  position:sticky;
  bottom:max(0px, env(safe-area-inset-bottom, 0px));
  z-index:2;
  padding:12px 14px calc(14px + env(safe-area-inset-bottom, 0px));
  border-top:1px solid rgba(15,23,42,.10);
  display:flex;
  flex-direction:column;
  gap:12px;
  background:#f8f8f8;
  box-shadow: 0 -8px 20px rgba(15,23,42,.05);
}
.cd-totals{ display:grid; gap:8px; }
.cd-total-row{ display:flex; align-items:center; justify-content:space-between; gap:10px; }
.cd-total-row--grand{
  padding-top:8px;
  border-top:1px solid rgba(15,23,42,.10);
}
.muted{ color:rgba(15,23,42,.55); font-size:12px; font-weight: 700; }
.cd-actions{ display:flex; gap:10px; }
.cd-actions .btn{
  font-size:14px;
}
.cd-accessories-link{
  text-decoration:none;
  justify-content:center;
}

.btn{
  height:40px;
  border:1px solid rgba(15,23,42,.12);
  background:#fff; padding:0 18px;
  cursor:pointer; font-weight:700;
  color: rgba(15,23,42,.85);
  display:inline-flex; align-items:center; gap:8px;
  border-radius:3px;
}
.btn:hover{ border-color: color-mix(in srgb, var(--pfe-color-accent, #e30b17) 40%, rgba(15,23,42,.12)); }
.btn.primary{
  background:var(--pfe-color-accent, #e30b17);
  border-color:var(--pfe-color-accent, #e30b17);
  color:#fff;
  box-shadow:none;
}
.btn.primary:hover{
  background:color-mix(in srgb, var(--pfe-color-accent, #e30b17) 92%, #000 8%);
  border-color:color-mix(in srgb, var(--pfe-color-accent, #e30b17) 92%, #000 8%);
}
.btn:disabled{ opacity:.5; cursor:not-allowed; box-shadow:none; }

.toast-wrap{
  position:relative;
  margin:0 0 10px;
  display:flex;
  flex-direction:column;
  align-items:center;
  gap:8px;
  pointer-events:none;
}
.toast{
  pointer-events:none;
  padding:10px 12px;
  border:1px solid rgba(15,23,42,.12);
  background:#fff;
  box-shadow: 0 10px 30px rgba(2,8,23,.10);
  font-size:13px;
  font-weight: 700;
  color: rgba(15,23,42,.86);
  max-width:min(100%, 320px);
  text-align:center;
}
.toast.success{ border-color: rgba(34,197,94,.25); background: rgba(34,197,94,.08); }
.toast.error{ border-color: rgba(239,68,68,.25); background: rgba(239,68,68,.08); }
.toast.info{ border-color: rgba(59,130,246,.25); background: rgba(59,130,246,.08); }

@media (max-width: 720px){
  .cd-backdrop{ padding:0; place-items:stretch; }
  .cd-backdrop.dropdown{
    position: absolute;
    inset: auto 0 auto auto;
    top: calc(100% + 8px);
    right: 8px;
  }
  .cd-modal{
    width:100vw;
    height:100dvh;
    max-height:100dvh;
    border:0;
    box-shadow:none;
  }
  .cd-backdrop.drawer .cd-modal{
    width:100vw;
    border:0;
    box-shadow:none;
  }
  .cd-backdrop.dropdown .cd-modal{
    width:min(94vw, 760px);
    height:min(70vh, 620px);
    max-height:min(70vh, 620px);
    border:1px solid rgba(15,23,42,.10);
    box-shadow: 0 22px 70px rgba(2,6,23,.25);
    border-radius:12px;
  }
}
</style>
